import * as React from "react";
import { Helmet } from "react-helmet";
import { useLocation, useParams } from "react-router-dom";
import { useForm } from "react-yup";
import * as Yup from "yup";

import { Checkbox, Select } from "~/ui";

import { Analytics } from "@skv/core";
import { usePageEnvelope } from "@skv/state";

import InPageAlert, {
  InPageAlertProps,
  InPageAlertVariant,
} from "~/ui/Alerts/InPageAlert";

import { OpportunityEoiSuccessViewModel } from "./types";

const SCHEMA = Yup.object({
  findUsId: Yup.number().nullable(),
  lowIncome: Yup.boolean().nullable(),
  jobSeeker: Yup.boolean().nullable(),
  nonEnglishBackground: Yup.boolean().nullable(),
  isIndigenous: Yup.boolean().nullable(),
  healthCondition: Yup.boolean().nullable(),
  hasDisability: Yup.boolean().nullable(),
  onlineApplicationId: Yup.number().required(),
}).defined();

const STATS_QUESTIONS = [
  {
    name: "lowIncome",
    label: "I am on a low income (less than $66,667 a year)",
  },
  {
    name: "jobSeeker",
    label: "I am looking for work",
  },
  {
    name: "nonEnglishBackground",
    label: "English was not my first language",
  },
  {
    name: "isIndigenous",
    label: "I am Aboriginal or Torres Straight Islander",
  },
  {
    name: "healthCondition",
    label: "I have a chronic health condition",
  },
  {
    name: "hasDisability",
    label: "I have a disability",
  },
];

const OpportunityDetailsEoiSuccess = (): JSX.Element => {
  const location = useLocation<OpportunityEoiSuccessViewModel>();

  const { id: routeOppId } = useParams<{
    id: string;
  }>();

  const { state } = location;

  const { pageEnvelope } = usePageEnvelope<OpportunityEoiSuccessViewModel>();
  const { data } = pageEnvelope;

  const mergedState = Object.assign({}, state, data);

  const [alert, setAlert] = React.useState<
    InPageAlertProps & { status: string }
  >(() => {
    if (mergedState.statsSuccessfullySent) {
      return {
        status: "sent",
        variant: InPageAlertVariant.Success,
        message: "Thank you. Your answers have successfully been sent.",
        show: true,
      };
    }

    return {
      status: "idle",
      variant: InPageAlertVariant.Success,
      message: "",
      show: false,
    };
  });

  const {
    FormProvider,
    field,
    values,
    isChecked,
    errors,
    touched,
    createSubmitHandler,
    isSubmitting,
    setSubmitting,
  } = useForm({
    validationSchema: SCHEMA,
    defaultValues: {
      onlineApplicationId: mergedState.onlineApplicationId,
      findUsId: 0,
      lowIncome: false,
      jobSeeker: false,
      hasDisability: false,
      healthCondition: false,
      isIndigenous: false,
      nonEnglishBackground: false,
    },
  });

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler((values) => {
      fetch(`/api/opportunity/${routeOppId}/stats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Network error, or response not ok");
        })
        .then(({ data, success, errorCode, errorMessage, meta }) => {
          if (success) {
            return setAlert((prev) => ({
              ...prev,
              status: "sent",
              variant: InPageAlertVariant.Success,
              message: "Thank you. Your answers have successfully been sent.",
              show: true,
            }));
          }
          if (errorCode) {
            throw Error("An unhandled error code has been sent.");
          }
        })
        .catch((err) => {
          setAlert((prev) => ({
            ...prev,
            status: "error",
            variant: InPageAlertVariant.Error,
            message:
              "Unfortunately we were not able to submit your answers. Please try again.",
            show: true,
          }));

          setSubmitting(false);
        });
    });
  }, [createSubmitHandler, setSubmitting, routeOppId]);

  return (
    <>
      <Helmet>
        <title>Thank you for applying</title>
      </Helmet>
      <div className="mb-4">
        <>
          <p>Thank you for offering to help with this opportunity.</p>
          <p>
            Your details have been sent to the organisation above. They will
            contact you soon with the next steps and details on how you can
            help.
          </p>
          <p>
            If you don't hear from them within a week you can contact them using
            the details on their organisation profile.
          </p>
        </>

        {/* Direct message from this organisation */}
        {mergedState.organisationMessage && (
          <>
            <h3 className="mt-4 fs-5">Direct message from this organisation</h3>
            <p
              css={`
                border-left: 5px solid ${(p) => p.theme.primaryColor};
                padding-left: 1.5rem;
                white-space: pre;
              `}
            >
              {mergedState.organisationMessage}
            </p>
          </>
        )}

        <>
          {alert.status === "sent" || alert.status === "error" ? (
            <InPageAlert
              variant={alert.variant}
              message={alert.message}
              show={alert.show}
            />
          ) : null}

          {/* One more question for statistical purposes*/}
          {mergedState?.statsQuestions && alert.status === "idle" && (
            <FormProvider>
              <form
                action={`/volunteering/${routeOppId}/stats`}
                method="POST"
                onSubmit={handleSubmit}
                className="nsw-form p-4 mt-4 bg-gray-200"
              >
                <h4 className="fs-5">
                  One more question for statistical purposes
                </h4>
                <p className="small">
                  Your answer to this question will not be included in your
                  application. The responses (not your personal details) will
                  help secure more support for volunteering.
                </p>

                <div className="nsw-form__group">
                  <fieldset className="nsw-form__fieldset mb-4">
                    <legend className="mb-1">
                      <span className="nsw-form__legend">
                        Personal background <small>(optional)</small>
                      </span>
                    </legend>
                    <div>
                      {STATS_QUESTIONS.map(({ name, label }, idx) => (
                        <Checkbox
                          key={idx}
                          type="checkbox"
                          id={name}
                          name={name}
                          label={label}
                          value={isChecked(name) ? 1 : 0}
                          checked={isChecked(name)}
                          {...field}
                        />
                      ))}
                    </div>
                  </fieldset>
                </div>

                <Select
                  id="findUsId"
                  name="findUsId"
                  label="How did you find this website"
                  optional
                  value={values.findUsId ?? 0}
                  {...field}
                >
                  <option disabled aria-disabled value={0}>
                    Please choose
                  </option>
                  {mergedState.statsQuestions.findUsList.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </Select>

                <input
                  type="hidden"
                  value={values.onlineApplicationId}
                  name="onlineApplicationId"
                />

                <button
                  type="submit"
                  className="btn btn-primary mt-4"
                  css={`
                    padding-left: 2.5rem;
                    padding-right: 2.5rem;
                  `}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </button>
              </form>
            </FormProvider>
          )}
        </>

        <Analytics
          data={{
            subSection: "opportunity",
            subSubSection: "opportunity:application+complete",
            subSubSubSection: "opportunity:application+complete",
            pageTitle: "opportunity:application+complete",
            pageType: "opportunity:form",
            opportunityId: Number(routeOppId),
            applicationId: values.onlineApplicationId,
          }}
        />
      </div>
    </>
  );
};

export default OpportunityDetailsEoiSuccess;
